import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import PeopleIcon from '@mui/icons-material/People';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BadgeIcon from '@mui/icons-material/Badge';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';

import SettingsIcon from '@mui/icons-material/Settings';
import CampaignIcon from '@mui/icons-material/Campaign';
import Alert from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';
import { getMatch, getShooters, getScores, getMatchStaff } from '../../api';
import Snackbar from '../../components/Snackbar';

function StatCard({ icon, title, value, color = 'primary.main', onClick }) {
  return (
    <Card
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { boxShadow: 4 } : {},
        transition: 'box-shadow 0.2s',
      }}
      onClick={onClick}
    >
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ color, display: 'flex' }}>{icon}</Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>{value}</Typography>
          <Typography variant="body2" color="text.secondary">{title}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function ManageHome() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [match, setMatch] = useState(null);
  const [shooterCount, setShooterCount] = useState(0);
  const [scoreCount, setScoreCount] = useState(0);
  const [staffCount, setStaffCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const [matchRes, shootersRes, scoresRes, staffRes] = await Promise.all([
          getMatch(matchId),
          getShooters(matchId).catch(() => ({ data: [] })),
          getScores(matchId).catch(() => ({ data: [] })),
          getMatchStaff(matchId).catch(() => ({ data: [] })),
        ]);
        setMatch(matchRes.data);
        setShooterCount(Array.isArray(shootersRes.data) ? shootersRes.data.length : 0);
        setScoreCount(Array.isArray(scoresRes.data) ? scoresRes.data.length : 0);
        setStaffCount(Array.isArray(staffRes.data) ? staffRes.data.length : 0);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load match data');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [matchId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const stageCount = match?.stages?.length || 0;

  const registrationLabel = {
    open: t('manage.regStatusOpen', 'Open'),
    closed: t('manage.regStatusClosed', 'Closed'),
    'open-code': t('manage.regStatusByCode', 'By Code'),
    'sold-out': t('manage.regStatusSoldOut', 'Sold Out'),
    'period-over': t('manage.regStatusPeriodOver', 'Period Over'),
  }[match?.registration] || match?.registration || '';

  const registrationColor = {
    open: 'success',
    closed: 'error',
    'open-code': 'warning',
    'sold-out': 'warning',
  }[match?.registration] || 'default';

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>{match?.title || 'Match Management'}</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip label={`${t('matches.registration')}: ${registrationLabel}`} color={registrationColor} size="small" />
          {match?.squadsPublished && <Chip label={t('manage.squadsPublished', 'Squads Published')} color="info" size="small" />}
          {match?.stagesPublished && <Chip label={t('manage.stagesPublished', 'Stages Published')} color="info" size="small" />}
        </Box>
      </Box>

      {/* Squad selection warning */}
      {match && match.registration !== 'closed' && (match.squads || []).length > 0 && (
        !match.squadsPublished || !match.squadSelection || match.squadSelection === 'closed'
      ) && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('manage.squadSelectionWarning', "Note: shooters can't pick squads when they register. To fix, make sure squad selection is turned on, and squads are published.")}
        </Alert>
      )}

      {/* Stats cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard
            icon={<PeopleIcon fontSize="large" />}
            title={t('matches.shooters')}
            value={shooterCount}
            color="primary.main"
            onClick={() => navigate(`/${matchId}/manage/shooters`)}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard
            icon={<SportsScoreIcon fontSize="large" />}
            title={t('matches.stages')}
            value={stageCount}
            color="secondary.main"
            onClick={() => navigate(`/${matchId}/manage/stages`)}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard
            icon={<AssessmentIcon fontSize="large" />}
            title={t('manage.scoresSubmitted', 'Scores Submitted')}
            value={scoreCount}
            color="success.main"
            onClick={() => navigate(`/${matchId}/manage/score-table`)}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard
            icon={<BadgeIcon fontSize="large" />}
            title={t('matches.staff')}
            value={staffCount}
            color="warning.main"
            onClick={() => navigate(`/${matchId}/manage/staff`)}
          />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* Quick actions */}
      <Typography variant="h6" gutterBottom>{t('manage.quickActions', 'Quick Actions')}</Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <Button
            variant="contained"
            fullWidth
            startIcon={<PlayArrowIcon />}
            onClick={() => navigate(`/${matchId}/manage/scores`)}
            sx={{ py: 2 }}
          >
            {t('scoring.score')}
          </Button>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<LeaderboardIcon />}
            onClick={() => navigate(`/${matchId}/manage/score-table`)}
            sx={{ py: 2 }}
          >
            {t('matches.results')}
          </Button>
        </Grid>

        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<SettingsIcon />}
            onClick={() => navigate(`/${matchId}/manage/settings`)}
            sx={{ py: 2 }}
          >
            {t('matches.settings')}
          </Button>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 3 }}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<CampaignIcon />}
            onClick={() => navigate(`/${matchId}/manage/announcements`)}
            sx={{ py: 2 }}
          >
            {t('matches.announcements')}
          </Button>
        </Grid>
      </Grid>

      <Snackbar open={!!error} message={error} severity="error" onClose={() => setError('')} />
    </Container>
  );
}
